// Apolo IDE — SessionFileTabs (Cursor-style FILE ctx-tabs).
//
// pos-s5 restructure — renders a row of FILE tabs in the chat chrome, one per
// file the agent has touched this session (read_file / write_file / edit /
// grep_search / list_files). The most-recent file is active (blue inset
// underline, from .rx-ctxtab.active); a written-but-not-yet-persisted file
// carries the unsaved dot (.rx-ctxdot via data-unsaved).
//
// Falls back to a single session-name tab when no files are known — fresh
// session, or after a reload (tool messages are live-only, so the touched-file
// list is rebuilt from the live turn, not persisted). Display-only for now:
// clicking a tab is a no-op until a file-pane/open-in-canvas wiring lands.

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Copy, FileText, FolderOpen, X } from 'lucide-react';

const FILE_TOOLS = new Set(['read_file', 'write_file', 'edit_file', 'apply_patch', 'grep_search', 'list_files']);
const WRITE_TOOLS = new Set(['write_file', 'edit_file', 'apply_patch']);

// Pure: derive the touched-file tabs from the live tool messages. Newest→oldest
// for recency, dedupe by path, cap to `max`. write/edit/patch → unsaved dot.
// Returned most-recent LAST so the last entry is the active tab. Exported so the
// derivation is unit-testable apart from the React tree.
export function deriveFileTabs(messages, max = 6) {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (let i = messages.length - 1; i >= 0 && out.length < max; i -= 1) {
    const m = messages[i];
    if (!m || m.role !== 'tool') continue;
    const tool = m.tool_name || '';
    if (!FILE_TOOLS.has(tool)) continue;
    const path = m.tool_args?.path ?? m.metadata?.path;
    if (typeof path !== 'string' || !path || seen.has(path)) continue;
    const base = path.split('/').pop() || '';
    // skip directory listings that don't point at a concrete file
    if (tool === 'list_files' && (path.endsWith('/') || !base.includes('.'))) continue;
    seen.add(path);
    out.push({ path, basename: base || path, isUnsaved: WRITE_TOOLS.has(tool) });
  }
  return out.reverse();
}

// best-effort clipboard write — jsdom / denied permissions just no-op.
function copyText(text) {
  if (!text) return;
  try {
    navigator?.clipboard?.writeText?.(String(text));
  } catch {
    /* clipboard unavailable — swallow */
  }
}

// Build the tab descriptors: one per touched file (most-recent active). O chip de
// sessão ("abra o servidor aqui") foi removido a pedido do Pedro — a barra de sessões
// já mostra a sessão. Sem arquivos → sem chip (só as abas extras, se houver).
function buildTabs(files) {
  if (!files || !files.length) return [];
  const last = files.length - 1;
  return files.map((f, i) => ({
    key: f.path,
    active: i === last,
    label: f.basename,
    title: f.path,
    name: f.basename,
    path: f.path,
    unsaved: !!f.isUnsaved,
  }));
}

// (sprint plan-tab / context-tab) — the header tab row can carry EXTRA tabs
// (Plano, Contexto) beside the session chip. `extraTabs` (or null) = array of
// { key, label, icon, active, badge?, onClick, testid?, title? }; when one is
// the active view the session chip de-activates (sessionActive=false) and
// `onSessionClick` switches back to the chat. All default to the prior behavior
// (single always-active session chip), so existing callers are unaffected.
const SessionFileTabs = memo(function SessionFileTabs({
  files,
  onBack,
  extraTabs = null,
}) {
  // right-click menu state: { x, y, name, path } or null
  const [menu, setMenu] = useState(null);
  const menuRef = useRef(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (!menu) return undefined;
    function onDocDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu();
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation(); // don't let Esc also fire a global "voltar"
        closeMenu();
      }
    }
    window.addEventListener('mousedown', onDocDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDocDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [menu, closeMenu]);

  const openMenu = useCallback((e, target) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, ...target });
  }, []);

  const tabs = buildTabs(files);

  return (
    <div
      className="rx-ctxtabs"
      role="tablist"
      aria-label="Arquivos tocados nesta sessão"
    >
      {/* (Pedro) — chevron "Voltar" E o chip de sessão removidos: a barra de sessões
         já mostra a sessão. "Fechar sessão" segue no menu de contexto das abas de arquivo. */}
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          className={`rx-ctxtab${t.active ? ' active' : ''}`}
          data-unsaved={t.unsaved ? 'true' : undefined}
          aria-selected={t.active}
          title={t.title}
          onContextMenu={(e) => openMenu(e, { name: t.name, path: t.path })}
        >
          <FileText size={13} aria-hidden="true" />
          <span className="truncate" style={{ maxWidth: 180 }}>{t.label}</span>
          <span className="rx-ctxdot" />
        </button>
      ))}

      {/* (sprint plan-tab / context-tab) — abas extras (Plano, Contexto) ao
          lado do chip da sessão. Cada uma é um chip rx-ctxtab com ícone e badge
          opcional; clicar troca a view. Origem dos dados: SessionWorkspace. */}
      {(Array.isArray(extraTabs) ? extraTabs : []).map((tab) => {
        const Icon = tab.icon;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            className={`rx-ctxtab${tab.active ? ' active' : ''}`}
            aria-selected={!!tab.active}
            title={tab.title || tab.label}
            data-testid={tab.testid}
            onClick={tab.onClick}
          >
            {Icon ? <Icon size={13} aria-hidden="true" /> : null}
            <span>{tab.label}</span>
            {tab.badge ? <span className="rx-ctxcount">{tab.badge}</span> : null}
          </button>
        );
      })}

      {menu && (
        <div
          ref={menuRef}
          className="rx-ctxmenu"
          role="menu"
          aria-label="Ações da aba"
          style={{ left: menu.x, top: menu.y }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              copyText(menu.name);
              closeMenu();
            }}
          >
            <Copy aria-hidden="true" /> Copiar nome
          </button>
          {menu.path && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                copyText(menu.path);
                closeMenu();
              }}
            >
              <FolderOpen aria-hidden="true" /> Copiar caminho
            </button>
          )}
          {onBack && (
            <>
              <div className="sep" aria-hidden="true" />
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={() => {
                  closeMenu();
                  onBack();
                }}
              >
                <X aria-hidden="true" /> Fechar sessão
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default SessionFileTabs;
